import { ProductAnalyticsItem } from "@babackapp/private-api-client/types";
import useTranslation from "@babackapp/translations/use-translation";
import { Button } from "@babackapp/ui/components/button";
import { Input } from "@babackapp/ui/components/input";
import { ShoppingCartIcon, XIcon } from "lucide-react";
import { useDashboardFilters } from "../lib/dashboard-filters";

type ProductSelectSearchProps = {
  search: string;
  onSearchChange: (search: string) => void;
  onClearSelection: () => void;
  selectedProduct: ProductAnalyticsItem | null;
};

/**
 * Search input for the dashboard products list.
 * Shows the selected product instead of the input when a product filter is
 * active, so the selection can be cleared.
 */
export function ProductSelectSearch({
  search,
  onSearchChange,
  onClearSelection,
  selectedProduct,
}: ProductSelectSearchProps) {
  const t = useTranslation();
  const { productId, variantId } = useDashboardFilters();

  const selectedVariant =
    selectedProduct?.variants?.find((v) => v.variantId === variantId) ?? null;

  if (productId) {
    const selectedValueText = selectedProduct
      ? selectedVariant?.title
        ? `${selectedProduct.title} - ${selectedVariant.title}`
        : selectedProduct.title
      : productId;

    return (
      <div className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-2 py-2 text-sm">
        <div className="flex gap-2 items-center truncate">
          {selectedProduct?.image ? (
            <img
              src={selectedProduct.image}
              alt={`Product image: ${selectedProduct.title}`}
              className="w-6 h-6 rounded-sm object-cover"
              loading="lazy"
            />
          ) : (
            <div className="w-6 h-6 rounded-sm bg-muted-foreground/20 flex items-center justify-center shrink-0">
              <ShoppingCartIcon className="w-3 h-3 text-muted-foreground/50" />
            </div>
          )}
          <span className="truncate">{selectedValueText}</span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 w-6 p-0"
          onClick={onClearSelection}
          aria-label="Clear product selection"
          title="Clear product selection"
        >
          <XIcon className="h-4 w-4" aria-hidden="true" />
        </Button>
      </div>
    );
  }

  return (
    <Input
      placeholder={t("productSelect.placeholder")}
      onChange={(e) => onSearchChange(e.target.value)}
      className="w-full"
      value={search}
      autoComplete="off"
      aria-label="Search products"
      aria-autocomplete="list"
      id="product-search-input"
    />
  );
}
